import React, { useState, useCallback } from 'react';
import { playbookApi } from '../../api/playbookApi';
import type { PlaybookTransitionAction } from '../../types/playbook.types';
import { ActionConfigForm } from './config-forms/ActionConfigForms';

interface ActionListPanelProps {
  transitionId: string;
  actions: PlaybookTransitionAction[];
  onRefresh: () => void;
}

const ACTION_TYPES = [
  'create_task', 'create_subtask', 'calculate_deadline', 'create_calendar_event',
  'create_reminder', 'send_notification', 'attach_form', 'resolve_court_rules',
  'generate_court_response_tasks', 'instantiate_template', 'trigger_playbook',
  'transition_sibling_playbook', 'update_field', 'set_runtime_data', 'webhook',
];

const ActionListPanel: React.FC<ActionListPanelProps> = ({ transitionId, actions, onRefresh }) => {
  const [addingType, setAddingType] = useState<string | null>(null);
  const [newConfig, setNewConfig] = useState<Record<string, unknown>>({});
  const [newIsAsync, setNewIsAsync] = useState(false);
  const [newErrorHandling, setNewErrorHandling] = useState<'log' | 'block' | 'rollback'>('log');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<Record<string, unknown>>({});
  const [editIsAsync, setEditIsAsync] = useState(false);
  const [editErrorHandling, setEditErrorHandling] = useState<'log' | 'block' | 'rollback'>('log');

  const sorted = [...actions].sort((a, b) => a.execution_order - b.execution_order);

  const handleAdd = useCallback(async () => {
    if (!addingType) return;
    try {
      await playbookApi.addAction(transitionId, {
        action_type: addingType,
        configuration: newConfig,
        execution_order: actions.length,
        is_async: newIsAsync,
        error_handling: newErrorHandling,
      });
      setAddingType(null);
      setNewConfig({});
      setNewIsAsync(false);
      setNewErrorHandling('log');
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, addingType, newConfig, newIsAsync, newErrorHandling, actions.length, onRefresh]);

  const handleUpdate = useCallback(async (actionId: string) => {
    try {
      await playbookApi.updateAction(transitionId, actionId, {
        configuration: editConfig,
        is_async: editIsAsync,
        error_handling: editErrorHandling,
      });
      setEditingId(null);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, editConfig, editIsAsync, editErrorHandling, onRefresh]);

  const handleRemove = useCallback(async (actionId: string) => {
    try {
      await playbookApi.removeAction(transitionId, actionId);
      onRefresh();
    } catch { /* silent */ }
  }, [transitionId, onRefresh]);

  const selectClass = 'rounded border border-gray-300 px-1 py-0.5 text-[10px] dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h5 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Actions ({actions.length})</h5>
        {!addingType && (
          <select
            className={selectClass}
            value=""
            onChange={(e) => { setAddingType(e.target.value); setNewConfig({}); }}
          >
            <option value="">+ Add action</option>
            {ACTION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {sorted.map((a) => (
        <div key={a.id} className="rounded border border-gray-200 dark:border-gray-700 p-2" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-1">
              <span className="text-[8px] text-gray-400 font-mono">#{a.execution_order}</span>
              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                {a.action_type}
              </span>
              {a.is_async && <span className="text-[8px] text-cyan-500">async</span>}
              <span className={`text-[8px] ${a.error_handling === 'block' ? 'text-rose-500' : a.error_handling === 'rollback' ? 'text-amber-500' : 'text-gray-400'}`}>
                {a.error_handling}
              </span>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={() => {
                setEditingId(a.id === editingId ? null : a.id);
                setEditConfig(a.configuration);
                setEditIsAsync(a.is_async);
                setEditErrorHandling(a.error_handling);
              }} className="text-[9px] text-lex-accent hover:text-lex-accent-hover">
                {editingId === a.id ? 'Close' : 'Edit'}
              </button>
              <button type="button" onClick={() => handleRemove(a.id)} className="text-[9px] text-rose-500 hover:text-rose-700">Remove</button>
            </div>
          </div>
          {editingId === a.id && (
            <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-700 pt-2">
              <ActionConfigForm actionType={a.action_type} config={editConfig} onChange={setEditConfig} />
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                  <input type="checkbox" checked={editIsAsync} onChange={(e) => setEditIsAsync(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600" />
                  Async
                </label>
                <select className={selectClass} value={editErrorHandling} onChange={(e) => setEditErrorHandling(e.target.value as any)}>
                  <option value="log">Log</option>
                  <option value="block">Block</option>
                  <option value="rollback">Rollback</option>
                </select>
              </div>
              <button type="button" onClick={() => handleUpdate(a.id)} className="rounded bg-lex-accent px-3 py-1 text-[10px] font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">Save</button>
            </div>
          )}
        </div>
      ))}

      {addingType && (
        <div className="rounded border border-amber-200 dark:border-amber-700 p-2 bg-amber-50/30 dark:bg-amber-900/10" style={{ animation: 'coverageDrillDown 200ms ease-out' }}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-medium text-amber-600 dark:text-amber-400">New: {addingType}</span>
            <button type="button" onClick={() => setAddingType(null)} className="text-[9px] text-gray-400 hover:text-gray-600">Cancel</button>
          </div>
          <ActionConfigForm actionType={addingType} config={newConfig} onChange={setNewConfig} />
          <div className="mt-2 flex items-center gap-3">
            <label className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
              <input type="checkbox" checked={newIsAsync} onChange={(e) => setNewIsAsync(e.target.checked)} className="rounded border-gray-300 dark:border-gray-600" />
              Async
            </label>
            <select className={selectClass} value={newErrorHandling} onChange={(e) => setNewErrorHandling(e.target.value as any)}>
              <option value="log">Log</option>
              <option value="block">Block</option>
              <option value="rollback">Rollback</option>
            </select>
          </div>
          <button type="button" onClick={handleAdd} className="mt-2 rounded bg-amber-600 px-3 py-1 text-[10px] font-medium text-white hover:bg-amber-700">Add Action</button>
        </div>
      )}
    </div>
  );
};

export default ActionListPanel;
